import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from 'layouts/AppLayout';
import Icon from 'components/AppIcon';
import { MOCK_FUNDS, MOCK_SPVS } from 'data/mockData';
import { getFundMetrics } from 'data/fundUtils';
import { ROUTES } from 'config/navConfig';

function StatusBadge({ status }) {
  const cfg = status === 'Closed'
    ? { bg: 'var(--color-success-subtle)', color: 'var(--color-success)', label: 'Closed' }
    : { bg: 'var(--color-warning-subtle)', color: 'var(--color-warning)', label: 'Raising' };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium font-caption"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      {cfg.label}
    </span>
  );
}

function MetricTile({ label, value, sub }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-caption" style={{ color: 'var(--color-muted-foreground)' }}>{label}</span>
      <span className="text-base font-semibold font-heading" style={{ color: 'var(--color-foreground)' }}>{value}</span>
      {sub && <span className="text-xs font-caption" style={{ color: 'var(--color-muted-foreground)' }}>{sub}</span>}
    </div>
  );
}

function FundCard({ fund }) {
  const navigate = useNavigate();
  const m = getFundMetrics(fund, MOCK_SPVS);

  const targetFmt   = `£${(fund.targetSize / 1_000_000).toFixed(0)}m`;
  const closedFmt   = fund.closedSize ? `£${(fund.closedSize / 1_000_000).toFixed(0)}m` : null;
  const softCircled = fund.raisedSoftCircled ? `£${(fund.raisedSoftCircled / 1_000_000).toFixed(0)}m soft-circled` : null;

  return (
    <div
      className="rounded-xl border border-border bg-card p-6 flex flex-col gap-5 cursor-pointer transition-shadow hover:shadow-md"
      style={{ background: 'var(--color-card)' }}
      onClick={() => navigate(ROUTES.fundDetail.replace(':id', fund.id))}
    >
      {/* Card header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: 'var(--color-primary-subtle)' }}
          >
            <Icon name="Landmark" size={18} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-foreground" style={{ fontSize: '1rem' }}>{fund.name}</h2>
            <p className="text-xs font-caption mt-0.5" style={{ color: 'var(--color-muted-foreground)' }}>
              {fund.fundManager} · {fund.domicile}
            </p>
          </div>
        </div>
        <StatusBadge status={fund.status} />
      </div>

      {/* Metrics row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-1 border-t border-border">
        <MetricTile
          label="Fund Size"
          value={closedFmt ?? targetFmt}
          sub={fund.status === 'Raising' ? `Target ${targetFmt}` : 'Final close'}
        />
        <MetricTile
          label={fund.status === 'Raising' ? 'Soft-Circled' : 'Capital Called'}
          value={fund.status === 'Raising' ? (softCircled ?? '—') : m.fmtCalled}
          sub={fund.status === 'Raising' ? `${((fund.raisedSoftCircled ?? 0) / fund.targetSize * 100).toFixed(0)}% of target` : `${m.calledPct}% called`}
        />
        <MetricTile
          label="LPs"
          value={m.lpCount}
          sub={fund.status === 'Raising' ? 'soft-circled' : 'committed'}
        />
        <MetricTile
          label="SPVs"
          value={m.spvCount}
          sub={fund.status === 'Raising' ? 'vehicles' : 'active vehicles'}
        />
      </div>

      {/* Performance row — Fund I only */}
      {fund.tvpiMultiple != null ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-border">
          <MetricTile label="Net IRR" value={`${fund.netIRR}%`} />
          <MetricTile label="Gross IRR" value={`${fund.grossIRR}%`} />
          <MetricTile label="TVPI" value={`${fund.tvpiMultiple}x`} />
          <MetricTile label="DPI" value={`${fund.dpiMultiple}x`} />
        </div>
      ) : (
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-border text-xs font-caption"
          style={{ color: 'var(--color-muted-foreground)' }}
        >
          <Icon name="Clock" size={13} color="var(--color-muted-foreground)" />
          Performance data will appear once capital is deployed
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between gap-4 pt-1">
        <div className="flex items-center gap-3 text-xs font-caption" style={{ color: 'var(--color-muted-foreground)' }}>
          <span>Inception {fund.inceptionDate}</span>
          {fund.finalCloseDate && <><span>·</span><span>Final close {fund.finalCloseDate}</span></>}
          <span>·</span>
          <span>{fund.fundTerm}-yr term</span>
        </div>
        <button
          className="flex items-center gap-1 text-sm font-medium font-caption"
          style={{ color: 'var(--color-primary)' }}
          onClick={(e) => { e.stopPropagation(); navigate(ROUTES.fundDetail.replace(':id', fund.id)); }}
        >
          View Fund <Icon name="ArrowRight" size={14} color="var(--color-primary)" />
        </button>
      </div>
    </div>
  );
}

export default function FundsListPage() {
  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        {/* Page header */}
        <div
          className="sticky top-0 bg-background border-b border-border px-6 md:px-10 lg:px-12 py-4 flex items-center justify-between gap-4"
          style={{ zIndex: 'var(--z-nav)' }}
        >
          <div className="pl-10 lg:pl-0">
            <h1 className="font-heading font-semibold text-foreground" style={{ fontSize: '1.375rem', lineHeight: 1.3 }}>
              Funds
            </h1>
            <p className="font-caption text-sm mt-0.5" style={{ color: 'var(--color-muted-foreground)' }}>
              {MOCK_FUNDS.length} funds under management · Greenfield Capital Partners LLP
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm font-caption" style={{ color: 'var(--color-muted-foreground)' }}>
            <Icon name="ShieldCheck" size={14} color="var(--color-muted-foreground)" />
            FCA Authorised
          </div>
        </div>

        {/* Content */}
        <div className="px-6 md:px-10 lg:px-12 py-8 space-y-6">
          {/* Summary bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Total AUM', value: '£375.5m', sub: 'NAV across active funds' },
              { label: 'Total Committed', value: '£340m', sub: 'LP commitments raised' },
              { label: 'Active SPVs', value: '9', sub: 'across both funds' },
              { label: 'LPs', value: '7', sub: 'institutional investors' },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-xl border border-border p-4"
                style={{ background: 'var(--color-card)' }}
              >
                <p className="text-xs font-caption" style={{ color: 'var(--color-muted-foreground)' }}>{s.label}</p>
                <p className="text-xl font-semibold font-heading mt-1" style={{ color: 'var(--color-foreground)' }}>{s.value}</p>
                <p className="text-xs font-caption mt-0.5" style={{ color: 'var(--color-muted-foreground)' }}>{s.sub}</p>
              </div>
            ))}
          </div>

          {/* Fund cards */}
          <div className="space-y-4">
            {MOCK_FUNDS.map((fund) => (
              <FundCard key={fund.id} fund={fund} />
            ))}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
